import { useCallback } from 'react';
import { min, round } from 'lodash';
import {
  Grid, InputAdornment, Stack, Typography,
} from '@mui/material';
import { useDispatch, useSelector } from '@client/hooks/redux.hooks';
import { useTicketingCheck } from '@client/hooks/useTicketingCheck';
import { useDebounce, useMountEffect } from '@client/hooks/utils.hooks';
import { getCurrentSKUAmount } from '@client/selectors/features/stripe';
import { setCurrentSKUAmount, setCurrentSKUPrice } from '@client/slices/features/stripe.reducer';
import { Description } from '@client/components/common/Description';
import { Hidden, Increment, TextField } from '@client/components/forms';
import { formatTotalAsCurrency, getCurrencySymbol } from '@sda/utils/lang';
import { Clamp } from '@client/components/common/Clamp';
import type { Product } from '@sda/interfaces/product';
import type { FetchDataType } from '@sda/sdk/interfaces';

interface TicketListItemProps extends Product, FetchDataType {
  readonly index: number;
}

export const TicketListItem: React.FC<TicketListItemProps> = ({ index, ...product }) => {
  const dispatch = useDispatch();
  const currentSKUAmount = useSelector(getCurrentSKUAmount);
  const capacityRemaining = product.capacity - (product.numTicketsPurchased || 0);

  const [badge, canPurchaseTickets] = useTicketingCheck({
    showId: product.showId,
    productId: product.id,
  });

  const handleSKUPriceChange = useDebounce((e) => {
    const { hasVariablePrice, minimumPrice } = product;

    let price = (e.target.value || 0) * 100;

    if (!hasVariablePrice || price >= minimumPrice) {
      dispatch(setCurrentSKUPrice({ price, productId: product.id }));
    }
  }, 200, [dispatch]);

  const updateTicketCount = useCallback((interval: number) => {
    const newCount = Math.max(0, currentSKUAmount[product.id] + interval);

    if (newCount > product.capacity || newCount > capacityRemaining) {
      return;
    }

    dispatch(setCurrentSKUAmount({ amount: newCount, productId: product.id }));
  }, [currentSKUAmount, product.id, product.capacity, capacityRemaining, dispatch]);

  // If we cannot purchase a ticket type, make sure it's QTY is 0
  useMountEffect(() => {
    if (!canPurchaseTickets) {
      dispatch(setCurrentSKUAmount({ amount: 0, productId: product.id }));
    }
  });

  return (
    <Stack spacing={2}>
      <div>
        <Clamp
          variant="subtitle2"
          color="textSecondary"
          gutterBottom={Boolean(product.description)}
          lines={2}
        >
          {product.name}
        </Clamp>

        {Boolean(product.description) && (
          <Description
            lines={3}
            color="textSecondary"
          >
            {product.description}
          </Description>
        )}

        <Hidden name={`prices[${index}].minimumPrice`} />

        <Hidden name={`prices[${index}].hasVariablePrice`} />
      </div>

      {canPurchaseTickets ? (
        <div>
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={6}
            >
              {product.hasVariablePrice ? (
                <TextField
                  name={`prices[${index}].ticketPrice`}
                  type="number"
                  label="Name your price"
                  helperText={`${formatTotalAsCurrency(product.minimumPrice, product.currency)} or more`}
                  onChange={handleSKUPriceChange}
                  fullWidth
                  forceHelperText
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {getCurrencySymbol(product.currency)}
                      </InputAdornment>
                    ),
                    inputProps: {
                      min: round(product.minimumPrice / 100, 2),
                      step: 0.01,
                    },
                  }}
                />
              ) : (
                <>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Price
                  </Typography>

                  <Typography
                    variant="h5"
                    color="textSecondary"
                  >
                    {product.minimumPrice === 0 ? 'Free' : formatTotalAsCurrency(product.minimumPrice, product.currency)}
                  </Typography>

                  <Hidden name="ticketPrice" />
                </>
              )}
            </Grid>

            <Grid item>
              <Increment
                name={`prices[${index}].quantity`}
                min={0}
                max={min([capacityRemaining, 10])}
                onChange={updateTicketCount}
                label="Quantity"
              />
            </Grid>
          </Grid>
        </div>
      ) : (
        <Typography
          variant="body2"
          color="error"
        >
          {badge.short}
        </Typography>
      )}
    </Stack>
  );
};
